import { Autocomplete, InputAdornment, Stack, TextField as MuiTextField } from '@mui/material';

import { useFieldArray, useFormContext } from 'FrontRoyalReactHookForm';
import { TextField } from 'FrontRoyalMaterialUiForm';
import {
    OpenInNewTabButton,
    RemoveButton,
    ReorderButtons,
    SectionHeading,
    SelectedChipList,
    renderOptionKeyedById,
    titleWithTag,
} from 'ContentItemEditor';
import AssetUrlText from './AssetUrlText';
import FileTypeIcon from './FileTypeIcon';
import ItemPanel from './ItemPanel';
import UploadFilesButton from './UploadFilesButton';
import { type EmbeddedLesson, type StreamFormValues } from './StreamEditor.types';

type Props = {
    // The lessons of this course, which are the only lessons a summary may cover.
    streamLessons: EmbeddedLesson[];
    uploading: boolean;
    onPickFiles: (files: File[]) => void;
};

// A summary is a PDF that covers some of the lessons of the course. The server rejects a summary
// that covers none, so each row keeps its own lesson list.
// oxlint-disable-next-line max-lines-per-function -- one row layout with its own lesson picker
export default function SummariesSection({ streamLessons, uploading, onPickFiles }: Props) {
    const { control, watch, setFieldValue } = useFormContext<StreamFormValues>();
    const { fields, remove, move } = useFieldArray({ control, name: 'summaries' });
    const summaries = watch('summaries');

    return (
        <Stack spacing={2}>
            <SectionHeading>Summaries</SectionHeading>

            {fields.map((field, index) => {
                const summary = summaries?.[index];
                const chosenLessonIds = summary?.lessons || [];
                const options = streamLessons.filter(lesson => !chosenLessonIds.includes(lesson.id));

                return (
                    <ItemPanel key={field.id}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <AssetUrlText url={summary?.url || ''} />

                            <Stack direction="row" sx={{ alignItems: 'center' }}>
                                {!!summary?.url && <OpenInNewTabButton href={summary.url} label="Test this link" />}

                                <ReorderButtons
                                    index={index}
                                    count={fields.length}
                                    label="this summary"
                                    onMove={move}
                                />

                                <RemoveButton label="Remove this summary" onClick={() => remove(index)} />
                            </Stack>
                        </Stack>

                        <Stack direction="row" spacing={1}>
                            <TextField
                                name={`summaries.${index}.title`}
                                label="Title for display"
                                fullWidth
                                slotProps={{ htmlInput: { dir: 'auto' } }}
                            />

                            {/* The filetype of a summary is always the uploaded pdf, so it is read
                                only here, as it was in the Angular form. */}
                            <TextField
                                name={`summaries.${index}.type`}
                                label="Filetype"
                                disabled
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FileTypeIcon type={summary?.type} />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Stack>

                        <Autocomplete
                            // Choosing a lesson appends it to the list below and clears the input.
                            value={null}
                            blurOnSelect
                            options={options}
                            getOptionLabel={titleWithTag}
                            isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
                            onChange={(_event, lesson) =>
                                lesson && setFieldValue(`summaries.${index}.lessons`, [...chosenLessonIds, lesson.id])
                            }
                            renderOption={renderOptionKeyedById(titleWithTag)}
                            renderInput={props => (
                                <MuiTextField {...props} name="add_lesson_to_summary" label="Add lesson" />
                            )}
                        />

                        <SelectedChipList
                            items={chosenLessonIds.map(lessonId => {
                                const lesson = streamLessons.find(candidate => candidate.id === lessonId);
                                return {
                                    key: lessonId,
                                    label: lesson ? titleWithTag(lesson) : lessonId,
                                };
                            })}
                            onRemove={lessonId =>
                                setFieldValue(
                                    `summaries.${index}.lessons`,
                                    chosenLessonIds.filter(candidate => candidate !== lessonId),
                                )
                            }
                        />
                    </ItemPanel>
                );
            })}

            <UploadFilesButton uploading={uploading} accept="application/pdf" onPick={onPickFiles} />
        </Stack>
    );
}
